import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE } from "../api/config";
import "./WarehouseTransferPanel.css";
import { resolveWarehouseStockBarcode } from "./plan-barcode.mjs";

// WAREHOUSE_TRANSFER_SCAN_MANUAL_COLOR_SIZE_V1
// WAREHOUSE_TRANSFER_ONE_BARCODE_INPUT_SCAN_MANUAL_V1
// WAREHOUSE_TRANSFER_COLOR_SIZE_STOCK_DROPDOWN_V1
// WAREHOUSE_TRANSFER_DRAFT_PERSIST_LOCAL_STORAGE_V1

const WAREHOUSE_BRAND = {
  DNGHT: "HANGTEN",
  AYMHT: "HANGTEN",
  DNGPF: "PRETTYFIT",
  AYMPF: "PRETTYFIT",
};
const SHOP_DESTINATIONS = {
  AYMHT: ["GMP", "JS", "MT", "JCHT", "MDYHT"],
  AYMPF: ["JCPF", "MDYPF"],
};
const WAREHOUSE_DESTINATIONS = {
  DNGHT: "AYMHT", AYMHT: "DNGHT",
  DNGPF: "AYMPF", AYMPF: "DNGPF",
};
const DEFAULT_WAREHOUSE_SOURCES = new Set(["DNGHT", "DNGPF"]);

function norm(value) {
  return String(value || "").trim().toUpperCase();
}

function cleanText(value) {
  return String(value || "").trim();
}

function warehouseBrand(code) {
  return WAREHOUSE_BRAND[norm(code)] || "";
}

function makeEmptyLine(brand = "") {
  return {
    productCode: "",
    brand: brand || "HANGTEN",
    color: "",
    size: "",
    qty: 1,
    sellingPriceMmk: 0,
    note: "",
    availableQty: 0,
  };
}

function formatQty(value) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString() + " Ks";
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function isWarehouseUser(user) {
  return ["WAREHOUSE_STAFF", "WAREHOUSE_MANAGER"].includes(norm(user?.role));
}

function normalizeProductCode(code) {
  return norm(code).replace(/\s+/g, "");
}

function normalizeCompare(value) {
  return norm(value).replace(/\s+/g, "");
}

function getCodeCandidates(code) {
  const base = normalizeProductCode(code);
  const list = [base];

  // Scanner တချို့မှာ 0410 code ကို 10410 လို leading 1 ပါလာနိုင်တာအတွက်
  // 10410-030-001-01 => 0410-030-001-01 candidate ထည့်
  if (/^1\d{4}-/.test(base)) {
    list.push(base.slice(1));
  }

  return Array.from(new Set(list.filter(Boolean)));
}

function hideOfficePlanTransferRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const planSource = String(row.planSource || row.plan_source || "").toUpperCase();
    const transferNo = String(row.transferNo || row.transfer_no || row.voucherNo || row.voucher_no || "").toUpperCase();

    if (planSource === "OFFICE_TRANSFER_PLAN") return false;
    if (transferNo.startsWith("OTP-")) return false;

    return true;
  });
}

// HIDE_OFFICE_PLAN_FROM_MANUAL_TRANSFER_LIST_V1


function isOfficePlanTransfer(row) {
  const planSource = String(row?.planSource || row?.plan_source || "").toUpperCase();
  const transferNo = String(
    row?.transferNo || row?.transfer_no || row?.voucherNo || row?.voucher_no || ""
  ).toUpperCase();

  return planSource === "OFFICE_TRANSFER_PLAN" || transferNo.startsWith("OTP-");
}

function manualTransferRowsOnly(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => !isOfficePlanTransfer(row));
}

// HIDE_OTP_OFFICE_TRANSFER_FROM_MANUAL_RENDER_V2

export default function WarehouseTransferPanel({ user, selectedLocation }) {
  const initialBrand = warehouseBrand(selectedLocation) || "HANGTEN";

  const [locations, setLocations] = useState([]);
  const [transferType, setTransferType] = useState("WAREHOUSE_TO_SHOP");
  const [fromLocationCode, setFromLocationCode] = useState(selectedLocation || "");
  const [toLocationCode, setToLocationCode] = useState("");
  const [requestNote, setRequestNote] = useState("");
  const [items, setItems] = useState([]);
  const [scanText, setScanText] = useState("");
  const [stockRows, setStockRows] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [scanMsg, setScanMsg] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeView, setActiveView] = useState("CREATE");
  const [isFocused, setIsFocused] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const draftLoadedRef = useRef("");
  const stockRequestRef = useRef(0);

  const scanRef = useRef(null);

  const allowedLocations = Array.isArray(user?.allowedLocations)
    ? user.allowedLocations.map(norm)
    : [];

  const fromBrand = warehouseBrand(fromLocationCode) || initialBrand;

  const draftKey = useMemo(() => {
    const uname = norm(user?.username);
    const loc = norm(selectedLocation || "DEFAULT");
    return uname ? `warehouseTransferDraft:${uname}:${loc}` : "";
  }, [user?.username, selectedLocation]);

  const warehouseLocations = useMemo(() => {
    return locations.filter((loc) => norm(loc.locationType) === "WAREHOUSE");
  }, [locations]);

  const shopLocations = useMemo(() => {
    return locations.filter((loc) => norm(loc.locationType) === "SHOP");
  }, [locations]);

  const sameBrandWarehouseLocations = useMemo(() => {
    return warehouseLocations.filter((loc) => norm(loc.code) === WAREHOUSE_DESTINATIONS[norm(fromLocationCode)]);
  }, [warehouseLocations, fromLocationCode]);

  const toLocationOptions = useMemo(() => {
    if (transferType === "WAREHOUSE_TO_SHOP") {
      const allowed = SHOP_DESTINATIONS[norm(fromLocationCode)] || [];
      return shopLocations.filter((loc) => allowed.includes(norm(loc.code)));
    }
    return sameBrandWarehouseLocations;
  }, [transferType, shopLocations, sameBrandWarehouseLocations, fromLocationCode]);

  const availableSkuCount = stockRows.length;
  const visibleTransfers = useMemo(() => manualTransferRowsOnly(transfers), [transfers]);
  const draftQty = items.reduce((total, item) => total + (Number(item.qty) || 0), 0);

  useEffect(() => {
    if (!isFocused) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsFocused(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isFocused]);

  function stockKey(row) {
    return [
      normalizeProductCode(row.productCode),
      normalizeCompare(row.color),
      normalizeCompare(row.size),
    ].join("|");
  }

  function findStockExact(productCode, color, size, rows = stockRows) {
    const codes = getCodeCandidates(productCode);

    return rows.find((row) => {
      return (
        codes.includes(normalizeProductCode(row.productCode)) &&
        normalizeCompare(row.color) === normalizeCompare(color) &&
        normalizeCompare(row.size) === normalizeCompare(size)
      );
    });
  }

  function getAvailableForItem(item, rows = stockRows) {
    if (!item?.productCode) return 0;

    if (item.color && item.size) {
      return Number(findStockExact(item.productCode, item.color, item.size, rows)?.qty || 0);
    }

    const codes = getCodeCandidates(item.productCode);

    return rows
      .filter((row) => {
        if (!codes.includes(normalizeProductCode(row.productCode))) return false;
        if (item.color && normalizeCompare(row.color) !== normalizeCompare(item.color)) return false;
        return true;
      })
      .reduce((sum, row) => sum + Number(row.qty || 0), 0);
  }

  function getColorOptionsForCode(productCode) {
    const codes = getCodeCandidates(productCode);

    return Array.from(
      new Set(
        stockRows
          .filter((row) => codes.includes(normalizeProductCode(row.productCode)) && Number(row.qty || 0) > 0)
          .map((row) => norm(row.color))
          .filter(Boolean)
      )
    ).sort();
  }

  function getSizeOptionsForCodeColor(productCode, color) {
    const codes = getCodeCandidates(productCode);

    return Array.from(
      new Set(
        stockRows
          .filter((row) => {
            return (
              codes.includes(normalizeProductCode(row.productCode)) &&
              normalizeCompare(row.color) === normalizeCompare(color) &&
              Number(row.qty || 0) > 0
            );
          })
          .map((row) => norm(row.size))
          .filter(Boolean)
      )
    ).sort();
  }

  function hydrateLine(line, rows = stockRows) {
    const exact = line.color && line.size ? findStockExact(line.productCode, line.color, line.size, rows) : null;

    return {
      ...line,
      brand: fromBrand,
      productCode: normalizeProductCode(line.productCode),
      color: norm(line.color),
      size: norm(line.size),
      availableQty: exact ? Number(exact.qty || 0) : getAvailableForItem(line, rows),
      sellingPriceMmk: exact ? Number(exact.priceMmk || 0) : Number(line.sellingPriceMmk || 0),
    };
  }

  async function loadLocations() {
    try {
      const response = await fetch(`${API_BASE}/api/erp/location-master`);
      const data = await response.json();

      const rows = Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.locations)
          ? data.locations
          : [];

      const normalized = rows
        .map((row) => ({
          code: norm(row.code || row.locationCode),
          name: row.name || row.code || row.locationCode || "",
          locationType: norm(row.locationType || row.location_type),
        }))
        .filter((row) => row.code && row.code !== "AYM");

      setLocations(normalized);
    } catch {
      setLocations(
        ["AYMHT", "AYMPF", "DNGHT", "DNGPF"].map((code) => ({
          code,
          name: code,
          locationType: "WAREHOUSE",
        }))
      );
    }
  }

  async function loadStock() {
    const requestId = ++stockRequestRef.current;
    if (!user?.username || !fromLocationCode) {
      setStockRows([]);
      return;
    }

    try {
      setStockLoading(true);
      setError("");

      const params = new URLSearchParams({
        username: user.username,
        location: fromLocationCode,
        brand: fromBrand || "ALL",
        positiveOnly: "true",
        stockScope: "TRANSFER_TEST",
        limit: "5000",
      });

      const response = await fetch(`${API_BASE}/api/erp/warehouse/inventory?${params.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load product stock.");
      }

      if (requestId !== stockRequestRef.current) return;

      const rows = Array.isArray(data.rows)
        ? data.rows
            .map((row) => ({
              productCode: normalizeProductCode(row.productCode || row.product_code),
              brand: norm(row.brand || fromBrand),
              color: norm(row.color),
              size: norm(row.size),
              qty: Number(row.qty || 0),
              priceMmk: Number(row.priceMmk || row.price_mmk || 0),
            }))
            .filter((row) => row.productCode && row.qty > 0)
        : [];

      setStockRows(rows);

      setItems((prev) => prev.map((item) => hydrateLine(item, rows)));
    } catch (err) {
      if (requestId !== stockRequestRef.current) return;
      setStockRows([]);
      setError(err.message);
    } finally {
      if (requestId === stockRequestRef.current) setStockLoading(false);
    }
  }

  async function loadTransfers() {
    if (!user?.username) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        username: user.username,
        location: selectedLocation || "",
        status: statusFilter,
        limit: "100",
      });

      const response = await fetch(`${API_BASE}/api/erp/warehouse/transfers?${params.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load transfers");
      }

      setTransfers(hideOfficePlanTransferRows(Array.isArray(data.rows) ? data.rows : []));
    } catch (err) {
      setError(err.message);
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }

  function syncItemBrand(nextBrand) {
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        brand: nextBrand || item.brand,
      }))
    );
  }

  function updateItem(index, field, value) {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        let next = {
          ...item,
          [field]: ["productCode", "color", "size"].includes(field) ? norm(value) : value,
          brand: fromBrand,
        };

        if (field === "productCode") {
          next = {
            ...next,
            productCode: normalizeProductCode(value),
            color: "",
            size: "",
            availableQty: 0,
          };
        }

        if (field === "color") {
          next = {
            ...next,
            color: norm(value),
            size: "",
          };
        }

        if (["productCode", "color", "size"].includes(field)) {
          return hydrateLine(next);
        }

        return next;
      })
    );
  }

  function addManualLine() {
    setItems((prev) => [...prev, makeEmptyLine(fromBrand)]);
    setTimeout(() => scanRef.current?.focus(), 50);
  }

  function removeLine(index) {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }

  function addOrIncrementLine(nextLine) {
    setItems((prev) => {
      const hydrated = hydrateLine(nextLine);
      const key = stockKey(hydrated);

      const existingIndex = prev.findIndex((row) => {
        return row.color && row.size && stockKey(row) === key;
      });

      if (existingIndex >= 0) {
        return prev.map((row, i) => {
          if (i !== existingIndex) return row;

          const availableQty = Number(row.availableQty || hydrated.availableQty || 0);
          const currentQty = Number(row.qty || 0);
          const nextQty = availableQty > 0 ? Math.min(currentQty + 1, availableQty) : currentQty + 1;

          return {
            ...row,
            qty: nextQty,
            availableQty,
          };
        });
      }

      return [...prev, hydrated];
    });
  }

  function handleScanSubmit(rawValue = scanText) {
    const result = resolveWarehouseStockBarcode(rawValue, stockRows);
    if (result.kind === "invalid") {
      setScanMsg(`Barcode ၏ product / color / size သည် ${fromLocationCode} stock နှင့် မကိုက်ပါ။`);
      return;
    }
    if (result.kind === "ambiguous") {
      setScanMsg("Barcode နှင့်ကိုက်သော stock row တစ်ခုထက်ပိုနေပါသည်။ စာရင်းကို ပြန်စစ်ပါ။");
      return;
    }

    if (result.kind === "exact") {
      const stock = result.item;
      addOrIncrementLine({
        productCode: stock.product_code,
        brand: fromBrand,
        color: stock.color,
        size: stock.size,
        qty: 1,
        sellingPriceMmk: stock.priceMmk,
        note: "",
        availableQty: stock.qty,
      });
      setScanMsg(`Added: ${stock.product_code} / ${stock.color} / ${stock.size} / Stock ${formatQty(stock.qty)}`);
      setScanText("");
      return;
    }

    // Code-only barcode: the operator must choose colour and size from stock.
    const stock = result.candidates[0];
    addOrIncrementLine({
      productCode: stock.product_code,
      brand: fromBrand,
      color: "",
      size: "",
      qty: 1,
      sellingPriceMmk: stock.priceMmk || 0,
      note: "",
      availableQty: getAvailableForItem({ productCode: stock.product_code }),
    });

    setScanMsg(`Code added: ${stock.product_code}. Color / Size ကို အောက်က stock စာရင်းမှ ရွေးပါ။`);
    setScanText("");
  }

  function validateTransfer(cleanItems) {
    if (!fromLocationCode || !toLocationCode) {
      throw new Error("Please select From and To location.");
    }

    if (!allowedLocations.includes(fromLocationCode)) {
      throw new Error("You can dispatch only from your login warehouse.");
    }

    if (!fromBrand) {
      throw new Error("From warehouse brand is not mapped. Use DNGHT/AYMHT/DNGPF/AYMPF.");
    }

    if (fromLocationCode === toLocationCode) {
      throw new Error("From and To location must be different.");
    }

    if (!toLocationOptions.some((loc) => loc.code === toLocationCode)) {
      throw new Error(`Route ${fromLocationCode} → ${toLocationCode} is not allowed. Select a destination shown in the list.`);
    }

    if (transferType === "WAREHOUSE_TO_WAREHOUSE") {
      const toBrand = warehouseBrand(toLocationCode);

      if (!toBrand) {
        throw new Error("Target warehouse brand is not mapped.");
      }

      if (toBrand !== fromBrand) {
        throw new Error("Cross brand warehouse transfer is blocked. Use same brand warehouse only.");
      }
    }

    if (cleanItems.length === 0) {
      throw new Error("Please scan or add at least one product item.");
    }

    const totalBySku = new Map();
    for (const item of cleanItems) {
      if (!item.productCode || !item.color || !item.size) {
        throw new Error("Product Code / Color / Size အကုန်ဖြည့်ပါ။ Barcode short ဖြစ်ရင် Color/Size ကို manual ရိုက်ပါ။");
      }
      if (!Number.isFinite(item.qty) || item.qty <= 0) {
        throw new Error(`Qty must be greater than zero: ${item.productCode} / ${item.color} / ${item.size}`);
      }

      const key = stockKey(item);
      totalBySku.set(key, (totalBySku.get(key) || 0) + item.qty);

      const stock = findStockExact(item.productCode, item.color, item.size);
      const available = Number(stock?.qty || 0);

      if (!stock || available <= 0) {
        throw new Error(`No stock in ${fromLocationCode}: ${item.productCode} / ${item.color} / ${item.size}`);
      }

      if (totalBySku.get(key) > available) {
        throw new Error(`Total qty exceeds stock: ${item.productCode} / ${item.color} / ${item.size}. Available ${available}`);
      }
    }
  }

  async function createTransfer() {
    try {
      setActionLoading("create");
      setError("");
      setSuccessMsg("");

      const cleanItems = items
        .map((item) => {
          const stock = findStockExact(item.productCode, item.color, item.size);

          return {
            productCode: normalizeProductCode(item.productCode),
            brand: fromBrand,
            color: norm(item.color),
            size: norm(item.size),
            qty: Number(item.qty || 0),
            sellingPriceMmk: Number(item.sellingPriceMmk || stock?.priceMmk || 0),
            note: item.note || "",
          };
        });

      validateTransfer(cleanItems);

      const response = await fetch(`${API_BASE}/api/erp/warehouse/transfers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('erpWarehouseTransferToken') || ''}`,
        },
        body: JSON.stringify({
          username: user.username,
          transferType,
          fromLocationCode,
          toLocationCode,
          requestNote,
          items: cleanItems,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Create transfer failed");
      }

      setSuccessMsg(`Transfer request created: ${data.transfer?.transferNo || ""}`);
      setActiveView("HISTORY");

      try {
        setDraftReady(false);
        localStorage.removeItem(draftKey);
      } catch {}

      setItems([]);
      setRequestNote("");
      setScanText("");
      setScanMsg("");

      setTimeout(() => setDraftReady(true), 0);

      await loadTransfers();
      await loadStock();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  async function actionTransfer(row, action) {
    try {
      setActionLoading(`${action}-${row.id}`);
      setError("");
      setSuccessMsg("");

      const endpoint = `${API_BASE}/api/erp/warehouse/transfers/${row.id}/${action}`;

      const body = {
        username: user.username,
      };

      if (action === "dispatch") body.dispatchNote = "Dispatched from warehouse UI";
      if (action === "receive") body.receiveNote = "Received from warehouse UI";

      const response = await fetch(endpoint, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('erpWarehouseTransferToken') || ''}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || `${action} failed`);
      }

      setSuccessMsg(data.message || `${action} success`);
      await loadTransfers();
      await loadStock();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    const nextFrom = selectedLocation || allowedLocations[0] || "";
    setFromLocationCode(nextFrom);
    setTransferType(DEFAULT_WAREHOUSE_SOURCES.has(norm(nextFrom)) ? "WAREHOUSE_TO_WAREHOUSE" : "WAREHOUSE_TO_SHOP");
    syncItemBrand(warehouseBrand(nextFrom));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, user?.username]);

  useEffect(() => {
    if (!draftKey) return;

    setDraftReady(false);
    draftLoadedRef.current = draftKey;
    setItems([]);
    setToLocationCode("");
    setRequestNote("");
    setScanText("");

    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const draft = JSON.parse(raw);

        if (draft.transferType) setTransferType(draft.transferType);

        const savedFrom = norm(draft.fromLocationCode);
        if (savedFrom && allowedLocations.includes(savedFrom)) {
          setFromLocationCode(savedFrom);
          syncItemBrand(warehouseBrand(savedFrom));
        }

        if (draft.toLocationCode) setToLocationCode(norm(draft.toLocationCode));
        if (typeof draft.requestNote === "string") setRequestNote(draft.requestNote);
        if (typeof draft.scanText === "string") setScanText(draft.scanText);
        if (draft.statusFilter) setStatusFilter(draft.statusFilter);

        if (Array.isArray(draft.items)) {
          setItems(
            draft.items.map((item) => ({
              ...item,
              productCode: normalizeProductCode(item.productCode),
              brand: norm(item.brand || fromBrand),
              color: norm(item.color),
              size: norm(item.size),
              qty: Number(item.qty || 1),
              sellingPriceMmk: Number(item.sellingPriceMmk || 0),
              note: item.note || "",
              availableQty: Number(item.availableQty || 0),
            }))
          );
        }
      }
    } catch (err) {
      console.warn("Failed to load warehouse transfer draft", err);
    }

    setTimeout(() => setDraftReady(true), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey || !draftReady || draftLoadedRef.current !== draftKey) return;

    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          transferType,
          fromLocationCode,
          toLocationCode,
          requestNote,
          scanText,
          statusFilter,
          items,
          savedAt: new Date().toISOString(),
        })
      );
    } catch (err) {
      console.warn("Failed to save warehouse transfer draft", err);
    }
  }, [
    draftKey,
    draftReady,
    transferType,
    fromLocationCode,
    toLocationCode,
    requestNote,
    scanText,
    statusFilter,
    items,
  ]);

  useEffect(() => {
    syncItemBrand(fromBrand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromBrand]);

  useEffect(() => {
    const valid = toLocationOptions.some((loc) => loc.code === toLocationCode);

    if (!valid) {
      setToLocationCode(toLocationOptions[0]?.code || "");
    }
  }, [toLocationOptions, toLocationCode]);

  useEffect(() => {
    loadStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, fromLocationCode, fromBrand]);

  useEffect(() => {
    loadTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, selectedLocation, statusFilter]);

  function canDispatch(row) {
    return (
      isWarehouseUser(user) &&
      row.status === "APPROVED" &&
      allowedLocations.includes(norm(row.fromLocationCode))
    );
  }

  function canReceive(row) {
    return (
      isWarehouseUser(user) &&
      row.status === "DISPATCHED" &&
      allowedLocations.includes(norm(row.toLocationCode))
    );
  }

  return (
    <div className={`warehouseTransferPanel warehouseTransferPanel--document ${isFocused ? "warehouseTransferPanel--focused" : ""}`}>
      <div className="warehouseTransferPanel__head">
        <div>
          <div className="warehouseTransferPanel__tag">INVENTORY <span>/</span> OPERATIONS <span>/</span> TRANSFERS</div>
          <h2>Transfers</h2>
          <p>Create stock movements and follow their progress.</p>
        </div>
        <div className="warehouseTransferPanel__headActions">
          <button type="button" className="warehouseTransferButton--quiet" onClick={() => { loadTransfers(); loadStock(); }} disabled={loading || stockLoading}>
            {loading || stockLoading ? "Loading..." : "Refresh"}
          </button>
          <button
            type="button"
            className="warehouseTransferButton--focus"
            onClick={() => setIsFocused((current) => !current)}
            aria-label={isFocused ? "Exit full screen" : "Open full screen"}
            aria-pressed={isFocused}
          >
            {isFocused ? "Exit Full Screen" : "Full Screen"}
          </button>
        </div>
      </div>

      <div className="warehouseTransferViewTabs" role="tablist" aria-label="Transfer workspace">
        <button type="button" role="tab" aria-selected={activeView === "CREATE"} className={activeView === "CREATE" ? "isActive" : ""} onClick={() => setActiveView("CREATE")}>New Transfer</button>
        <button type="button" role="tab" aria-selected={activeView === "HISTORY"} className={activeView === "HISTORY" ? "isActive" : ""} onClick={() => setActiveView("HISTORY")}>Transfer Orders <span>{visibleTransfers.length}</span></button>
      </div>

      {error && <div className="warehouseTransferAlert warehouseTransferAlert--error">{error}</div>}
      {successMsg && <div className="warehouseTransferAlert warehouseTransferAlert--success">{successMsg}</div>}

      {activeView === "CREATE" && (isWarehouseUser(user) ? (
        <div className="warehouseTransferCreate">
          <div className="warehouseTransferCreateTop">
            <div>
              <span className="warehouseTransferEyebrow">NEW INTERNAL TRANSFER</span>
              <h3>{transferType === "WAREHOUSE_TO_SHOP" ? "Warehouse to Shop" : "Warehouse to Warehouse"}</h3>
              <p>Set the route, add products, and submit for approval.</p>
            </div>
            <div className="warehouseTransferContext">
              <span>Source <b>{fromLocationCode || "-"}</b></span>
              <span>Brand <b>{fromBrand || "-"}</b></span>
              <span>Stock SKUs <b>{availableSkuCount}</b></span>
            </div>
          </div>

          <div className="warehouseTransferStatusFlow" aria-label="Transfer process">
            <span className="isCurrent">Draft</span>
            <span>Requested</span>
            <span>Approved</span>
            <span>Dispatched</span>
            <span>Received</span>
          </div>

          <div className="warehouseTransferRouteBox">
            <div className="warehouseTransferStep"><span>01</span><div><strong>General Information</strong><small>Choose the operation type and destination.</small></div></div>
            <div className="warehouseTransferTypeBtns">
              <button
                type="button"
                className={transferType === "WAREHOUSE_TO_SHOP" ? "isActive" : ""}
                onClick={() => setTransferType("WAREHOUSE_TO_SHOP")}
              >
                Warehouse → Shop
              </button>
              <button
                type="button"
                className={transferType === "WAREHOUSE_TO_WAREHOUSE" ? "isActive" : ""}
                onClick={() => setTransferType("WAREHOUSE_TO_WAREHOUSE")}
              >
                Warehouse → Warehouse
              </button>
            </div>

            <div className="warehouseTransferRouteGrid">
              <label>
                <span>From Warehouse</span>
                <select
                  value={fromLocationCode}
                  onChange={(e) => {
                    const next = norm(e.target.value);
                    setFromLocationCode(next);
                    setTransferType((previous) => previous === "WAREHOUSE_TO_WAREHOUSE" || DEFAULT_WAREHOUSE_SOURCES.has(next)
                      ? "WAREHOUSE_TO_WAREHOUSE" : "WAREHOUSE_TO_SHOP");
                    syncItemBrand(warehouseBrand(next));
                    setItems([]);
                  }}
                >
                  {allowedLocations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc} - {warehouseBrand(loc) || "UNKNOWN"}
                    </option>
                  ))}
                </select>
              </label>

              <div className="warehouseTransferArrow">→</div>

              <label>
                <span>Send To</span>
                <select value={toLocationCode} onChange={(e) => setToLocationCode(norm(e.target.value))}>
                  {toLocationOptions.map((loc) => (
                    <option key={loc.code} value={loc.code}>
                      {loc.code} - {loc.name}
                      {warehouseBrand(loc.code) ? ` (${warehouseBrand(loc.code)})` : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="warehouseTransferNoteFull">
              <span>Transfer Note</span>
              <input value={requestNote} onChange={(e) => setRequestNote(e.target.value)} placeholder="Optional note" />
            </label>
          </div>

          <div className="warehouseTransferStep warehouseTransferStep--outside"><span>02</span><div><strong>Barcode Entry</strong><small>Scan a product or type its barcode.</small></div></div>
          <div className="warehouseTransferScanBox">
            <label>
              <span>Product barcode</span>
              <input
                ref={scanRef}
                value={scanText}
                onChange={(e) => setScanText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleScanSubmit();
                  }
                }}
                placeholder="Scan barcode or type manually here, then press Enter"
                autoComplete="off"
              />
            </label>

            <button type="button" onClick={() => handleScanSubmit()}>
              Add Product
            </button>

          </div>

          <div className="warehouseTransferScanHelp">
            <span>
              Short barcode? Select its color and size in the table below.
            </span>
            {scanMsg && <b>{scanMsg}</b>}
          </div>

          <div className="warehouseTransferReviewHead">
            <div className="warehouseTransferStep"><span>03</span><div><strong>Operations</strong><small>Review each product before submitting.</small></div></div>
            <div className="warehouseTransferReviewCount">{items.length} lines · {formatQty(draftQty)} units</div>
          </div>
          <div className="warehouseTransferItemTableWrap">
            <table className="warehouseTransferItemTable">
              <thead>
                <tr>
                  <th>Product Code</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th>On Hand</th>
                  <th>Demand</th>
                  <th>Price (Ks)</th>
                  <th>Line Note</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="warehouseTransferEmptyRow">
                      <strong>No products added yet</strong>
                      <span>Scan a barcode above to add the first operation line.</span>
                    </td>
                  </tr>
                ) : (
                  items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          value={item.productCode}
                          onChange={(e) => updateItem(index, "productCode", e.target.value)}
                          placeholder="Product code"
                        />
                      </td>

                      <td>
                        <select
                          value={item.color}
                          onChange={(e) => updateItem(index, "color", e.target.value)}
                          disabled={!item.productCode}
                        >
                          <option value="">Select color</option>
                          {getColorOptionsForCode(item.productCode).map((color) => (
                            <option key={color} value={color}>
                              {color}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td>
                        <select
                          value={item.size}
                          onChange={(e) => updateItem(index, "size", e.target.value)}
                          disabled={!item.productCode || !item.color}
                        >
                          <option value="">Select size</option>
                          {getSizeOptionsForCodeColor(item.productCode, item.color).map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className={Number(item.availableQty || 0) > 0 ? "stockOkCell" : "stockBadCell"}>
                        {formatQty(item.availableQty)}
                      </td>

                      <td>
                        <input
                          type="number"
                          min="1"
                          max={Number(item.availableQty || 999999)}
                          value={item.qty}
                          onChange={(e) => updateItem(index, "qty", e.target.value)}
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          min="0"
                          value={item.sellingPriceMmk}
                          onChange={(e) => updateItem(index, "sellingPriceMmk", e.target.value)}
                        />
                      </td>

                      <td>
                        <input
                          value={item.note}
                          onChange={(e) => updateItem(index, "note", e.target.value)}
                          placeholder="Note"
                        />
                      </td>

                      <td>
                        <button type="button" aria-label={`Remove ${item.productCode || "product"}`} onClick={() => removeLine(index)}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="warehouseTransferCreate__actions">
            <span>{items.length === 0 ? "Scan a product to begin." : `${formatQty(draftQty)} units ready for review`}</span>
            <button
              type="button"
              onClick={createTransfer}
              disabled={actionLoading === "create" || items.length === 0}
            >
              {actionLoading === "create" ? "Creating..." : "Create Transfer Request"}
            </button>
          </div>
        </div>
      ) : (
        <div className="warehouseTransferOfficeNote">
          Office/Admin user. Transfer approval is handled from main ERP Inventory.
        </div>
      ))}

      {activeView === "HISTORY" && <>
      <div className="warehouseTransferListHead">
        


        <h3>Transfer List</h3>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">All Status</option>
          <option value="PENDING">PENDING</option>
          <option value="APPROVED">APPROVED</option>
          <option value="DISPATCHED">DISPATCHED</option>
          <option value="COMPLETED">COMPLETED</option>
        </select>
      </div>

      <div className="warehouseTransferList">
        {visibleTransfers.length === 0 ? (
          <div className="warehouseTransferEmpty">No transfer yet.</div>
        ) : (
          visibleTransfers.map((row) => (
            <div key={row.id} className="warehouseTransferCard">
              <div className="warehouseTransferCard__top">
                <div>
                  <b>{row.transferNo}</b>
                  <span>{row.transferType}</span>
                </div>

                <strong className={`status-${row.status}`}>{row.status}</strong>
              </div>

              <div className="warehouseTransferRoute">
                <span>{row.fromLocationCode}</span>
                <b>→</b>
                <span>{row.toLocationCode}</span>
              </div>

              <div className="warehouseTransferMeta">
                <span>Items: {row.itemRows}</span>
                <span>Total Qty: {formatQty(row.totalQty)}</span>
                <span>Requested: {formatDate(row.requestedAt)}</span>
              </div>

              <div className="warehouseTransferItemsMini">
                {(row.items || []).slice(0, 5).map((item) => (
                  <span key={item.id}>
                    {item.productCode} / {item.color || "-"} / {item.size} × {formatQty(item.qty)}
                  </span>
                ))}
              </div>

              <div className="warehouseTransferCard__actions">
                {canDispatch(row) && (
                  <button
                    type="button"
                    onClick={() => actionTransfer(row, "dispatch")}
                    disabled={actionLoading === `dispatch-${row.id}`}
                  >
                    🚚 Dispatch
                  </button>
                )}

                {canReceive(row) && (
                  <button
                    type="button"
                    onClick={() => actionTransfer(row, "receive")}
                    disabled={actionLoading === `receive-${row.id}`}
                  >
                    📦 Receive
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      </>}
    </div>
  );
}

// PRODUCT_TRANSFER_HIDE_OFFICE_ASSIGNED_SECTION_HARD_REMOVE_V2 removed=1
